const INTENT_STORAGE_KEY = 'millet.guestBrowseIntent';
const PROFILE_VIEW_STORAGE_KEY = 'millet.guestProfileViewIds';
const BANNER_DISMISS_STORAGE_KEY = 'millet.guestSignupBannerDismissed';

export const GUEST_SIGNUP_BANNER_PROFILE_VIEWS = 3;

export const GUEST_ACCESS_COPY = {
  browse: {
    title: 'Log in or sign up',
    message: 'Create a free brand account to view creator profiles and browse everyone on Millet.',
    primaryLabel: 'Sign up',
    userType: 'brand',
  },
  saveCreator: {
    title: 'Save creators you love',
    message: 'Create a free account to keep your favorite creators in one place.',
    primaryLabel: 'Create free account',
    userType: 'brand',
  },
  invite: {
    title: 'Connect with this creator',
    message: 'Create a free brand account to send invitations and start collaborating.',
    primaryLabel: 'Create brand account',
    userType: 'brand',
  },
  message: {
    title: 'Message this creator',
    message: 'Create a free brand account to send a message and start collaborating.',
    primaryLabel: 'Create brand account',
    userType: 'brand',
  },
  apply: {
    title: 'Apply to this campaign',
    message: 'Join Millet to apply, connect with brands, and manage your collaborations.',
    primaryLabel: 'Join as a creator',
    userType: 'influencer',
  },
  saveCampaign: {
    title: 'Save campaigns you love',
    message: 'Create a free account to keep campaigns you want to join in one place.',
    primaryLabel: 'Join as a creator',
    userType: 'influencer',
  },
  follow: {
    title: 'Follow this brand',
    message: 'Create a free account to follow brands and hear about new campaigns.',
    primaryLabel: 'Join as a creator',
    userType: 'influencer',
  },
};

export function getGuestAccessCopy(intent) {
  return GUEST_ACCESS_COPY[intent] || GUEST_ACCESS_COPY.browse;
}

function readJson(storage, key) {
  try {
    const raw = storage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function safeInternalPath(value) {
  if (typeof value !== 'string' || !value.startsWith('/') || value.startsWith('//')) return null;
  if (value.includes('://') || value.includes('\\')) return null;
  return value;
}

function safeSearch(search) {
  if (typeof search !== 'string' || search === '') return '';
  if (!search.startsWith('?')) return '';
  if (search.includes('://') || search.includes('\\')) return '';
  return search;
}

export function rememberGuestBrowseIntent({
  pathname,
  search = '',
  intent = null,
  entityId = null,
}) {
  const safePath = safeInternalPath(pathname);
  if (!safePath) return null;
  const payload = {
    pathname: safePath,
    search: safeSearch(search),
    intent,
    entityId: entityId == null ? null : String(entityId),
  };
  try {
    sessionStorage.setItem(INTENT_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    return payload;
  }
  return payload;
}

export function readGuestBrowseIntent() {
  const parsed = readJson(sessionStorage, INTENT_STORAGE_KEY);
  const pathname = safeInternalPath(parsed?.pathname);
  if (!pathname) return null;
  return {
    ...parsed,
    pathname,
    search: safeSearch(parsed.search || ''),
  };
}

export function guestReturnPath(intent = readGuestBrowseIntent()) {
  if (!intent?.pathname) return null;
  return safeInternalPath(`${intent.pathname}${safeSearch(intent.search || '')}`);
}

export function resolveAuthReturnTo(fromLocation) {
  const fromPath = safeInternalPath(fromLocation?.pathname);
  if (fromPath) {
    return safeInternalPath(`${fromPath}${safeSearch(fromLocation?.search || '')}`) || fromPath;
  }
  return guestReturnPath();
}

export function clearGuestBrowseIntent() {
  try {
    sessionStorage.removeItem(INTENT_STORAGE_KEY);
  } catch {
    // Ignore storage failures; navigation can still use the query return path.
  }
}

export function isGuestSignupBannerDismissed() {
  try {
    return localStorage.getItem(BANNER_DISMISS_STORAGE_KEY) === '1';
  } catch {
    return true;
  }
}

export function dismissGuestSignupBanner() {
  try {
    localStorage.setItem(BANNER_DISMISS_STORAGE_KEY, '1');
  } catch {
    // A failed write only means the banner can appear again this browser.
  }
}

export function recordGuestProfileView(profileId) {
  if (profileId == null || isGuestSignupBannerDismissed()) return false;
  const existing = readJson(sessionStorage, PROFILE_VIEW_STORAGE_KEY);
  const ids = new Set(Array.isArray(existing) ? existing.map(String) : []);
  ids.add(String(profileId));
  try {
    sessionStorage.setItem(PROFILE_VIEW_STORAGE_KEY, JSON.stringify([...ids]));
  } catch {
    return ids.size >= GUEST_SIGNUP_BANNER_PROFILE_VIEWS;
  }
  return ids.size >= GUEST_SIGNUP_BANNER_PROFILE_VIEWS;
}
